import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, node, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { TextInput } from '@dojo/widgets/text-input';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, lccstate, pathmap, searchOpen } from 'lcc-state';
import { throttle } from 'lodash-es';
import { publish } from 'pub-sub';
import { css } from '../css/search.m.css';
import { svc } from '../support/searchsvc';
const nls = createNlsMiddleware('settings');
const factory = create({
    icache: createICacheMiddleware(),
    theme,
    lccstate,
    nls,
    node
});
export const Search = factory(({ middleware: { icache, lccstate, theme, nls, node } }) => {
    const { isPlaceholder, messages } = nls();
    if (isPlaceholder) {
        return null;
    }
    const themeCss = theme.classes(css);
    const inputWrapperNode = node.get('input-wrapper');
    if (inputWrapperNode) {
        const inputEl = inputWrapperNode.querySelector('input');
        if (inputEl) {
            inputEl.setAttribute('role', 'combobox');
        }
    }
    const selectOption = (kw) => {
        icache.set('showingKeywords', false);
        icache.set('selected', kw);
        icache.set('topics', []);
        topic_list().then(() => {
            setTimeout(() => {
                document.getElementById('search-topics-result')?.focus();
            }, 0);
        });
    };
    const focusInput = () => {
        const wrapper = document.querySelector('[xqa="search wrapper"]');
        wrapper?.querySelector('input')?.focus();
    };
    const setKeywords = throttle(async () => {
        const find = icache.getOrSet('currentInput', '');
        if (find.length > 0) {
            const language = window.hcstatic.portal_language || 'en';
            const { get, path } = lccstate;
            const version = get(path('bookversion'));
            const kwlist = await svc.keyword_search({ find, version, language });
            icache.set('resultCount', kwlist.length);
            icache.set('keywords', kwlist.map((kw, idx) => {
                const optionId = `search-option-${idx}`;
                return v('li', {
                    key: optionId,
                    id: optionId,
                    role: 'option',
                    'aria-selected': 'false',
                    tabIndex: -1,
                    onfocus: (event) => {
                        event.currentTarget.setAttribute('aria-selected', 'true');
                    },
                    onblur: (event) => {
                        event.currentTarget.setAttribute('aria-selected', 'false');
                    },
                    onclick: (event) => {
                        event && event.stopPropagation();
                        selectOption(kw);
                    },
                    onkeydown: (event) => {
                        const listEl = document.getElementById('search-results-list');
                        const options = listEl
                            ? Array.from(listEl.querySelectorAll('[role="option"]'))
                            : [];
                        const currentIndex = options.indexOf(event.currentTarget);
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                selectOption(kw);
                                break;
                            case Keys.DownArrow:
                                event.preventDefault();
                                if (currentIndex < options.length - 1) {
                                    options[currentIndex + 1].focus();
                                }
                                break;
                            case Keys.UpArrow:
                                event.preventDefault();
                                if (currentIndex > 0) {
                                    options[currentIndex - 1].focus();
                                }
                                else {
                                    focusInput();
                                }
                                break;
                            case Keys.Escape:
                                event.preventDefault();
                                event.stopPropagation();
                                icache.set('showingKeywords', false);
                                focusInput();
                                break;
                            case Keys.Home:
                                event.preventDefault();
                                options[0]?.focus();
                                break;
                            case Keys.End:
                                event.preventDefault();
                                options[options.length - 1]?.focus();
                                break;
                        }
                    }
                }, [kw]);
            }));
        }
        else {
            icache.set('keywords', []);
            icache.set('resultCount', 0);
        }
    }, 1100, { trailing: true });
    const keyword_list = () => {
        setKeywords();
    };
    const topic_list = async () => {
        const language = window.hcstatic.portal_language || 'en';
        const { get, path } = lccstate;
        const version = get(path('bookversion'));
        const topicids = await svc.topics_for_keyword({
            keyword: icache.getOrSet('selected', ''),
            version,
            language
        });
        icache.set('topics', topicids.map((topicid) => {
            const km = keymap.get(topicid);
            const pm = pathmap.get(km.path);
            const { topicref: topic } = pm.topic;
            return w('TopicRef', {
                topic,
                messages,
                showCheckbox: false,
                action: () => {
                    searchOpen({ searchOpen: false });
                    publish('close-all/panes');
                }
            });
        }));
    };
    const render_topics = () => {
        return v('fieldset', {
            key: 'search-topics',
            id: 'search-topics-result',
            tabIndex: -1,
            classes: [themeCss.resultSet]
        }, [
            v('legend', { classes: [themeCss.resultHead] }, [icache.get('selected')]),
            v('div', { classes: [themeCss.topicList] }, icache.get('topics'))
        ]);
    };
    const showing = icache.get('showingKeywords');
    const keywords = icache.get('keywords');
    const resultCount = icache.get('resultCount') || 0;
    const noResults = showing && keywords !== undefined && keywords.length === 0;
    const liveText = showing && resultCount > 0
        ? `${resultCount} result${resultCount !== 1 ? 's' : ''} available. Use down arrow to browse.`
        : noResults
            ? 'No search terms found.'
            : '';
    return v('div', { xqa: 'search wrapper', classes: [themeCss.root] }, [
        v('div', {
            ...formatAriaProperties({ live: 'polite', atomic: 'true' }),
            styles: {
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: '0',
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: '0'
            }
        }, [liveText]),
        v('div', {
            key: 'input-wrapper',
            classes: [themeCss.inputWrapper],
            onkeydown: (event) => {
                switch (event.key) {
                    case Keys.Escape:
                        if (icache.get('showingKeywords')) {
                            event.preventDefault();
                            event.stopPropagation();
                            icache.set('showingKeywords', false);
                        }
                        break;
                    case Keys.DownArrow:
                        if (icache.get('showingKeywords')) {
                            event.preventDefault();
                            const first = document.querySelector('#search-results-list [role="option"]');
                            first?.focus();
                        }
                        break;
                }
            },
            onkeyup: (event) => {
                if (event.key === Keys.Escape) {
                    event.stopPropagation();
                }
            }
        }, [
            w(TextInput, {
                key: 'search-inp',
                type: 'text',
                value: icache.get('currentInput'),
                aria: {
                    controls: 'search-results-list',
                    expanded: showing ? 'true' : 'false',
                    haspopup: 'listbox',
                    autocomplete: 'list',
                    activedescendant: ''
                },
                onKeyDown: (keyCode, preventDefault) => {
                    switch (keyCode) {
                        case 27:
                            preventDefault();
                            if (icache.get('showingKeywords')) {
                                icache.set('showingKeywords', false);
                            }
                            break;
                        case 40:
                            if (icache.get('showingKeywords')) {
                                preventDefault();
                                const first = document.querySelector('#search-results-list [role="option"]');
                                first?.focus();
                            }
                            break;
                    }
                },
                onValue: (value) => {
                    icache.set('currentInput', value);
                    icache.set('showingKeywords', value !== '');
                    keyword_list();
                }
            }, [
                {
                    label: v('p', { xqa: 'search-instructions', classes: [themeCss.instructions] }, [
                        messages['search_instructions']
                    ])
                }
            ]),
            icache.get('currentInput')
                ? v('span', {
                    tabIndex: 0,
                    role: 'button',
                    ...formatAriaProperties({
                        label: `${showing ? 'Collapse' : 'Expand'} the list of search results`
                    }),
                    classes: [themeCss.listButton, showing ? themeCss.listActive : themeCss.listInactive],
                    onclick: (event) => {
                        event && event.stopPropagation();
                        const current = icache.get('currentInput');
                        if (current) {
                            icache.set('showingKeywords', !showing);
                        }
                    },
                    onkeydown: (event) => {
                        const current = icache.get('currentInput');
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                if (current) {
                                    icache.set('showingKeywords', !showing);
                                }
                                break;
                        }
                    }
                }, [
                    v('i', {
                        classes: ['fas', showing ? 'fa-chevron-down' : 'fa-chevron-left'],
                        ...formatAriaProperties({ hidden: 'true' })
                    })
                ])
                : null
        ]),
        showing
            ? v('ul', {
                key: 'search-keywords',
                id: 'search-results-list',
                xqa: 'search keywords',
                role: 'listbox',
                ...formatAriaProperties({ label: 'Search results' }),
                classes: [css.searchResults],
                onkeyup: (event) => {
                    if (event.key === Keys.Escape) {
                        event.stopPropagation();
                    }
                }
            }, noResults
                ? [v('li', { key: 'no-results', styles: { cursor: 'default' } }, ['No search terms found'])]
                : keywords || [])
            : render_topics()
    ]);
});
